import * as Sentry from '@sentry/react';
import { env } from '../config/env';

/**
 * Credential-bearing share links: the path segment after these prefixes is a
 * share ID (a UUID) that authorizes unauthenticated access to a candidate,
 * referee, or certificate. It is a credential, so it is redacted from every
 * Sentry event (URLs, breadcrumbs, messages, transactions, span data) before
 * sending. The UUID-only patterns keep authenticated routes such as
 * /candidate/dashboard untouched; PUBLIC_API covers the plural fetch paths
 * the browser calls (e.g. /api/v1/public/candidates/<uuid>).
 *
 * The boundary `(?=$|[/?#])` redacts the UUID even when the public link is
 * followed by a sub-path, query string, or fragment, e.g.
 * /candidate/<uuid>?utm=x, /referee/<uuid>#form, /certificate/<uuid>?download=1.
 */
const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const SENSITIVE_ROUTE = new RegExp(`\\/(candidate|referee|certificate)\\/${UUID}(?=$|[/?#])`, 'g');
const SENSITIVE_PUBLIC_API = new RegExp(
  `\\/public\\/(candidates|referees)\\/${UUID}(?=$|[/?#])`,
  'g'
);

export function redactSensitiveRoutes(text: string): string {
  return text
    .replace(SENSITIVE_ROUTE, '/$1/:shareId')
    .replace(SENSITIVE_PUBLIC_API, '/public/$1/:shareId');
}

/**
 * Redacts share IDs from the surfaces shared by both error events and
 * transactions: request URL, breadcrumbs, event message, and transaction name.
 */
function scrubBaseEvent(event: Sentry.Event): void {
  scrubRequestUrl(event);
  event.breadcrumbs?.forEach(scrubBreadcrumb);
  if (event.message) {
    event.message = redactSensitiveRoutes(event.message);
  }
  if (event.transaction) {
    event.transaction = redactSensitiveRoutes(event.transaction);
  }
}

/**
 * Fail-closed: if scrubbing throws, return null (drop the event) rather than
 * risk sending an unsanitized credential. Sentry treats a null return from a
 * beforeSend hook as "do not send".
 */
function scrubErrorEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  try {
    scrubBaseEvent(event);
    event.exception?.values?.forEach(scrubException);
    return event;
  } catch {
    return null;
  }
}

function scrubTransactionEvent<T extends Sentry.Event>(event: T): T | null {
  try {
    scrubBaseEvent(event);
    event.spans?.forEach(scrubSpan);
    return event;
  } catch {
    return null;
  }
}

function scrubRequestUrl(event: Sentry.Event): void {
  if (event.request?.url) {
    event.request.url = redactSensitiveRoutes(event.request.url);
  }
}

function scrubBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  if (breadcrumb.message) {
    breadcrumb.message = redactSensitiveRoutes(breadcrumb.message);
  }
  scrubDataValues(breadcrumb.data);
}

function scrubException(exception: Sentry.Exception): void {
  if (exception.value) {
    exception.value = redactSensitiveRoutes(exception.value);
  }
}

function scrubSpan(span: { description?: string; data?: Record<string, unknown> }): void {
  if (span.description) {
    span.description = redactSensitiveRoutes(span.description);
  }
  scrubDataValues(span.data);
}

/** Recursively redacts share IDs from structured data (objects, arrays, nested). */
function scrubDataValues(data: Record<string, unknown> | undefined): void {
  if (!data) {
    return;
  }
  for (const key of Object.keys(data)) {
    data[key] = scrubNested(data[key]);
  }
}

function scrubNested(value: unknown): unknown {
  if (typeof value === 'string') {
    return redactSensitiveRoutes(value);
  }
  if (Array.isArray(value)) {
    return value.map(scrubNested);
  }
  if (value && typeof value === 'object') {
    const nested = value as Record<string, unknown>;
    for (const key of Object.keys(nested)) {
      nested[key] = scrubNested(nested[key]);
    }
  }
  return value;
}

export function initSentry(): void {
  if (!env.VITE_SENTRY_DSN) {
    return;
  }

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_SENTRY_ENVIRONMENT,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    beforeSend: scrubErrorEvent,
    beforeSendTransaction: scrubTransactionEvent,
  });
}

export function captureException(error: unknown): void {
  Sentry.captureException(error);
}

export function captureMessage(message: string): void {
  Sentry.captureMessage(message);
}
